/** Stores: rename one, or merge a shop read under two names into one. */

import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/ui/Field";
import { SettingsHeader } from "@/features/settings/SettingsHeader";
import { CatalogRow } from "./CatalogRow";
import { mergeTargets, storesQuery, useMergeStores, useRenameStore } from "./queries";

export function StoresPage() {
  const { t } = useTranslation();
  const stores = useQuery(storesQuery);
  const rename = useRenameStore();
  const merge = useMergeStores();

  return (
    <section className="space-y-5">
      <SettingsHeader
        title={t("catalog.stores.title")}
        description={t("catalog.stores.subtitle")}
      />

      {stores.isPending && <p className="text-stone-500">{t("common.loading")}</p>}
      {stores.isError && <Notice tone="error">{t("settings.errors.loadFailed")}</Notice>}
      {stores.data?.length === 0 && <Notice tone="info">{t("catalog.stores.empty")}</Notice>}

      {stores.data && stores.data.length > 0 && (
        <ul className="divide-y divide-stone-200 overflow-hidden rounded-lg border border-stone-200 bg-white">
          {stores.data.map((store) => (
            <CatalogRow
              key={store.id}
              item={store}
              title={
                <Link
                  to="/settings/stores/$storeId"
                  params={{ storeId: store.id }}
                  className="hover:underline"
                >
                  {store.name}
                </Link>
              }
              detail={[t("catalog.stores.receipts", { count: store.receipt_count }), store.address]
                .filter(Boolean)
                .join(" · ")}
              targets={mergeTargets(stores.data, store.id)}
              onRename={(name) => rename.mutateAsync({ id: store.id, name })}
              onMerge={(intoId) => merge.mutateAsync({ id: store.id, intoId })}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
